/**
 * Dataset Assistant V2 component.
 *
 * In compact mode (sidebar), renders a 52px toolbar with a dropdown
 * ConversationSelector, new-analysis button, and custom header actions.
 * In fullscreen mode (!compactMode), renders a full left-hand
 * ConversationSidebar and a minimal header with only the header actions.
 */
import React, { type ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { HttpAgent } from "@ag-ui/client";
import type { AssistantRuntime } from "@assistant-ui/react";
import { AssistantRuntimeProvider } from "@assistant-ui/react";
import { useAgUiRuntime } from "@assistant-ui/react-ag-ui";
import { useFliptBoolean } from "@flipt-io/flipt-client-react";
import { createDatasetAssistantApi } from "datasets/components/assistant/api/DatasetAssistantApi";
import useDataset from "datasets/hooks/useDataset";
import { Loader2, ServerCrash, SquarePen } from "lucide-react";
import { Thread, type ThreadProps } from "~/components/assistant-v2/Thread";
import { useAssistantFeatures } from "~/components/assistant-v2/useAssistantFeatures";
import { useContextBuffer } from "~/components/assistant-v2/useContextBuffer";
import { useConversationName } from "~/components/assistant-v2/useConversationName";
import { useStreamingMessageId } from "~/components/assistant-v2/useMessageFeedback";
import { useRunError } from "~/components/assistant-v2/useRunError";
import { useSkillSync } from "~/components/assistant-v2/useSkillSync";
import { AssistantProvider } from "~/components/assistant/AssistantContext";
import { ConversationSelector } from "~/components/assistant/ConversationSelector";
import { IconButton } from "~/components/assistant/PopoverButton";
import { filterVisibleServers } from "~/components/assistant/hooks/useMcpServers";
import { Tooltip, TooltipContent, TooltipTrigger } from "~/components/ui/tooltip";
import useUser from "~/contexts/user";
import { cn } from "~/lib/utils";
import { trackEvent } from "~/utils/monitoring";
import { ConversationSidebar } from "./ConversationSidebar";
import { useConversationHistory } from "./useConversationHistory";
import { useConversationManager } from "./useConversationManager";
import { useRunCancellation } from "./useRunCancellation";

const { VITE_ANALYSIS_AGENT_API } = import.meta.env;
const api = createDatasetAssistantApi(VITE_ANALYSIS_AGENT_API);

export interface DatasetAssistantV2Props {
  datasetId: string;
  className?: string;
  headerActions?: ReactNode;
  compactMode?: boolean;
}

export function DatasetAssistantV2({
  datasetId,
  className,
  headerActions,
  compactMode = false,
}: DatasetAssistantV2Props) {
  const user = useUser();
  const debugMode = useFliptBoolean("agent-debug-mode", false, user?.email);
  const { data: dataset } = useDataset(datasetId);
  const datasetTitle = dataset?.title ?? datasetId;

  const suggestions = useMemo(() => {
    const prompts = dataset?.samplePrompts
      ?.filter((p) => p.trim().length > 0)
      .map((p) => ({ title: p, prompt: p }));
    return prompts && prompts.length > 0 ? prompts : undefined;
  }, [dataset?.samplePrompts]);

  const {
    conversations,
    currentConversationId,
    isLoading: isLoadingConversations,
    isCreating,
    loadError,
    createConversation,
    deleteConversation,
    selectConversation,
    renameConversation,
    updateConversationName,
  } = useConversationManager({ api, datasetId });

  const {
    skillsEnabled,
    mcpServers,
    selectedMcpServers,
    toggleMcpServer,
    systemSkills,
    userSkills,
    isLoadingSkills,
    createSkill,
    updateSkill,
    deleteSkill,
    getSkill,
    refreshSkills,
  } = useAssistantFeatures({ api, contextId: datasetId, enableSkills: true });

  const visibleServers = useMemo(
    () => filterVisibleServers(mcpServers, dataset?.externalMcpAccess ?? []),
    [mcpServers, dataset?.externalMcpAccess]
  );

  const agent = useMemo(() => {
    if (!currentConversationId) {
      return new HttpAgent({ url: "about:blank", headers: { Accept: "text/event-stream" } });
    }
    return new HttpAgent({
      url: `${VITE_ANALYSIS_AGENT_API}/conversations/${currentConversationId}/messages_agui`,
      headers: { Accept: "text/event-stream" },
    });
  }, [currentConversationId]);

  const runtime = useAgUiRuntime({
    agent,
    logger: {
      debug: (...args: any[]) => console.debug("[AG-UI]", ...args),
      error: (...args: any[]) => console.error("[AG-UI]", ...args),
    },
  });

  const sendPrompt = useCallback(
    (prompt: string) => {
      runtime.thread.append({ role: "user", content: [{ type: "text", text: prompt }] });
    },
    [runtime]
  );

  const contextBuffer = useContextBuffer(agent);
  const { error: runError, clearError: clearRunError } = useRunError(agent);
  const streamingFeedbackMessageId = useStreamingMessageId(agent);
  useConversationName(agent, updateConversationName);
  useSkillSync(agent, refreshSkills);
  const { isLoadingHistory } = useConversationHistory(runtime, currentConversationId);

  useEffect(() => {
    trackEvent("assistant_v2_ui_opened", { dataset_id: datasetId, context: "dataset" });
    return () => {
      trackEvent("assistant_v2_ui_closed", { dataset_id: datasetId, context: "dataset" });
    };
  }, [datasetId]);

  const handleNewChat = useCallback(async () => {
    trackEvent("assistant_v2_new_chat_clicked", { dataset_id: datasetId, context: "dataset" });
    await createConversation();
  }, [datasetId, createConversation]);

  const handleConversationSelect = useCallback(
    (conversationId: string) => {
      if (conversationId === currentConversationId) {
        return;
      }
      trackEvent("assistant_v2_conversation_selected", {
        dataset_id: datasetId,
        conversation_id: conversationId,
        context: "dataset",
      });
      selectConversation(conversationId);
    },
    [datasetId, currentConversationId, selectConversation]
  );

  const handleDeleteConversation = useCallback(
    (conversationId: string, event: React.MouseEvent) => {
      trackEvent("assistant_v2_conversation_deleted", {
        dataset_id: datasetId,
        conversation_id: conversationId,
        context: "dataset",
      });
      deleteConversation(conversationId, event);
    },
    [datasetId, deleteConversation]
  );

  const handleRenameConversation = useCallback(
    (conversationId: string, displayName: string) => {
      trackEvent("assistant_v2_conversation_renamed", {
        dataset_id: datasetId,
        conversation_id: conversationId,
        context: "dataset",
      });
      renameConversation(conversationId, displayName);
    },
    [datasetId, renameConversation]
  );

  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  const handleToggleSidebar = useCallback(() => {
    setIsSidebarCollapsed((prev) => {
      trackEvent("assistant_v2_sidebar_toggled", {
        dataset_id: datasetId,
        context: "dataset",
        collapsed: !prev,
      });
      return !prev;
    });
  }, [datasetId]);

  if (isLoadingConversations) {
    return (
      <div className={cn("flex h-full items-center justify-center bg-white", className)}>
        <Loader2 className="size-6 text-gray-400 animate-spin" />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className={cn("flex h-full items-center justify-center bg-white", className)}>
        <div className="flex flex-col items-center gap-3 text-center">
          <ServerCrash className="size-10 text-gray-400" />
          <p className="text-lg font-medium text-gray-700">Analysis Agent is unavailable</p>
          <p className="text-sm text-gray-500">
            Please try again later or contact support if the issue persists.
          </p>
        </div>
      </div>
    );
  }

  const threadContent = (
    <AssistantProvider
      api={api}
      conversationId={currentConversationId}
      contextId={datasetId}
      debugMode={debugMode}
      sendPrompt={sendPrompt}
    >
      {currentConversationId ? (
        <AssistantRuntimeProvider runtime={runtime}>
          <RuntimeThread
            api={api}
            runtime={runtime}
            agent={agent}
            clearRunError={clearRunError}
            conversationId={currentConversationId}
            contextId={datasetId}
            suggestions={suggestions}
            contextBuffer={contextBuffer}
            isLoadingHistory={isLoadingHistory}
            runError={runError}
            streamingFeedbackMessageId={streamingFeedbackMessageId}
            skillsEnabled={skillsEnabled}
            systemSkills={systemSkills}
            userSkills={userSkills}
            isLoadingSkills={isLoadingSkills}
            onCreateSkill={createSkill}
            onUpdateSkill={updateSkill}
            onDeleteSkill={deleteSkill}
            onGetSkill={getSkill}
            mcpServers={visibleServers}
            selectedMcpServers={selectedMcpServers}
            onToggleMcpServer={toggleMcpServer}
            showToolSelector={true}
            placeholder={`Ask anything about ${datasetTitle}...`}
          />
        </AssistantRuntimeProvider>
      ) : null}
    </AssistantProvider>
  );

  return (
    <div className={cn("flex h-full bg-white", compactMode ? "flex-col" : "flex-row", className)}>
      {!compactMode && (
        <ConversationSidebar
          conversations={conversations}
          currentConversationId={currentConversationId}
          onNewChat={handleNewChat}
          onSelect={handleConversationSelect}
          onDelete={handleDeleteConversation}
          onRename={handleRenameConversation}
          onToggle={handleToggleSidebar}
          isCollapsed={isSidebarCollapsed}
        />
      )}

      <div className="flex flex-col h-full min-w-0 w-full">
        <div className="flex flex-row items-center justify-between px-4 h-[52px] min-h-[52px] shrink-0 w-full border-b border-black/[0.12]">
          {compactMode ? (
            <div className="flex flex-row gap-0.5 items-center">
              <ConversationSelector
                conversations={conversations}
                currentConversationId={currentConversationId}
                isCreatingConversation={isCreating}
                onSelect={handleConversationSelect}
                onDelete={handleDeleteConversation}
                onRename={handleRenameConversation}
              />
            </div>
          ) : (
            <div />
          )}

          <div className="flex flex-row gap-0.5 items-center justify-end">
            {compactMode && (
              <Tooltip delayDuration={700}>
                <TooltipTrigger asChild>
                  <IconButton
                    onClick={handleNewChat}
                    disabled={isCreating}
                    variant="ghost"
                    aria-label="New Analysis"
                  >
                    <SquarePen className="size-4.5 text-gray-600" />
                  </IconButton>
                </TooltipTrigger>
                <TooltipContent>
                  <p>New Analysis</p>
                </TooltipContent>
              </Tooltip>
            )}
            {headerActions}
          </div>
        </div>

        <div className="flex-1 min-h-0">{threadContent}</div>
      </div>
    </div>
  );
}

/**
 * Inner component rendered inside AssistantRuntimeProvider so that
 * useRunCancellation can access useAuiState.
 */
function RuntimeThread({
  api: apiProp,
  runtime,
  agent: agentProp,
  clearRunError,
  conversationId,
  ...threadProps
}: ThreadProps & {
  api: typeof api;
  runtime: AssistantRuntime;
  agent: HttpAgent;
  clearRunError: () => void;
}) {
  const cancellation = useRunCancellation({
    api: apiProp,
    agent: agentProp,
    runtime,
    conversationId,
    clearRunError,
  });

  return (
    <Thread
      key={conversationId ?? undefined}
      conversationId={conversationId}
      {...cancellation}
      {...threadProps}
    />
  );
}
